<script>
  import { onDestroy, onMount, tick } from "svelte";
  import { getNotificationsContext } from "svelte-notifications";
  import Papa from "papaparse";
  import DialogBase2 from "../../../../components/dialog/DialogBase2.svelte";
  import DialogTitle from "../../../../components/dialog/DialogTitle.svelte";
  import DialogSplitter from "../../../../components/dialog/DialogSplitter.svelte";
  import DialogContent from "../../../../components/dialog/DialogContent.svelte";
  import DialogContentInput from "../../../../components/dialog/DialogContentInput.svelte";
  import HelpTooltip from "../../../../components/tooltip/HelpTooltip.svelte";
  import RadioGroup from "../../../../components/input/RadioGroup.svelte";
  import DialogBottomControls from "../../../../components/dialog/DialogBottomControls.svelte";
  import DialogBottomControlButton from "../../../../components/dialog/DialogBottomControlButton.svelte";

  const {addNotification} = getNotificationsContext()

  export let onClose

  export let onClickUpload

  /**
   * @type {"shift-jis"|"utf-8"|"utf-8_bom"}
   */
  let fileEncoding = "shift-jis"

  /**
   * @type {"yes"|"no"}
   */
  let doesHeaderRowExist = "yes"

  let loadedRows = []
  let fileContentString = ""

  async function handleChangeFile(event) {
    const files = event.target.files ?? []
    if (files.length === 0) return
    const buffer = await files[0].arrayBuffer()

    // clear result rows
    loadedRows = []

    const textDecoder = new TextDecoder(fileEncoding === "shift-jis" ? "shift-jis" : "utf-8")

    // Read the given file content assuming it is in the encoding specified with "fileEncoding"
    try {
      fileContentString = textDecoder.decode(buffer)
    } catch (e) {
      addNotification({
        text: "ファイルの読み込みに失敗しました。適切なファイルエンコーディングが指定されているか、ファイルの内容が適切か確認してください。",
        position: "top-right",
        removeAfter: 4000,
        type: "error",
      });
    }

    // remove BOM from the content. This is applied intentionally even if the specified fileEncoding is "utf-8" (without bom)
    if (fileContentString.charCodeAt(0) === 0xFEFF) fileContentString = fileContentString.slice(1)

    let parsedRows
    try {
      const parseResult = Papa.parse(fileContentString)
      if (parseResult.meta.aborted || parseResult.errors.length > 0) {
        console.debug(parseResult)
        return addNotification({
          text: "ファイルの読み込みに失敗しました。ファイルの内容が適切か確認してください。",
          position: "top-right",
          removeAfter: 4000,
          type: "error",
        })
      }
      parsedRows = parseResult.data
    } catch (e) {
      console.debug(e)
      return addNotification({
        text: "ファイルの読み込みに失敗しました。ファイルの内容が適切か確認してください。",
        position: "top-right",
        removeAfter: 4000,
        type: "error",
      })
    }

    // if "doesHeaderRowExist" is true, ignore the first row
    loadedRows = doesHeaderRowExist ? parsedRows.slice(1) : parsedRows
  }

  let dialog

  onMount(async () => {
    await tick()
    dialog.showModal()
  })
  onDestroy(() => dialog.close())
</script>

<DialogBase2 bind:dialog={dialog} width="600px">
  <DialogTitle>データをアップロードする</DialogTitle>
  <DialogSplitter />
  <DialogContent>
    <DialogContentInput type="file" onChange={handleChangeFile} accept=".csv">
      <HelpTooltip title="アップロード可能なファイル">
        csv 形式のファイルがアップロード可能です。
      </HelpTooltip>
    </DialogContentInput>
    <div class="settings">
      <div class="setting">
        ファイルエンコーディング:
        <RadioGroup
          bind:value={fileEncoding}
          options={[
            {value: "utf-8", text: "UTF-8"},
            {value: "utf-8_bom", text: "UTF-8 (BOM付き)"},
            {value: "shift-jis", text: "Shift-JIS"}
          ]}
        />
      </div>
      <div class="setting">
        ヘッダ行の有無:
        <RadioGroup
          bind:value={doesHeaderRowExist}
          options={[
            {value: "yes", text: "有"},
            {value: "no", text: "無"}
          ]}
        />
      </div>
    </div>
  </DialogContent>
  <DialogBottomControls>
    <DialogBottomControlButton onClick={() => onClickUpload(loadedRows, fileContentString)} disabled={loadedRows.length === 0}>
      アップロード
    </DialogBottomControlButton>
    <DialogBottomControlButton type="secondary" onClick={onClose}>
      キャンセル
    </DialogBottomControlButton>
  </DialogBottomControls>
</DialogBase2>

<style>
  .settings {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  .setting {
    display: inline-flex;
    gap: 1rem;
    flex-wrap: wrap;
    font-size: .9rem
  }
</style>
